import { motion } from 'framer-motion'
import { t } from 'i18next'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useEtracker } from '../../../hooks/use-etracker'
import { Button } from '../../button/button'
import { RadioButtonGroup } from '../../radio-button-group/radio-button-group'
import { TextArea } from '../../text-area/text-area'
import styles from '../exit-feedback-modal.module.css'

interface ProcessingExitFormProps {
	onSubmit: () => void
	exitButtonSlot?: React.ReactNode
}

type ProcessingExitReasons = 'searchTooLong' | 'notExpected' | 'other'

export const ProcessingExitForm: React.FC<ProcessingExitFormProps> = ({ onSubmit, exitButtonSlot }) => {
	const {
		register,
		handleSubmit,
		watch,
		control,
		formState: { errors },
	} = useForm()
	const { trackExitEvent } = useEtracker()

	const watchedExitReason = watch('exit-reason')

	const options: {
		value: ProcessingExitReasons
		label: string
		append?: React.ReactNode
	}[] = [
		{
			value: 'searchTooLong',
			label: t('exitFeedback.formProcessing.reason.options.searchTooLong'),
		},
		{
			value: 'notExpected',
			label: t('exitFeedback.formProcessing.reason.options.notExpected'),
		},
		{
			value: 'other',
			label: t('exitFeedback.formProcessing.reason.options.other'),
			append: (
				<motion.div
					animate={{
						opacity: watchedExitReason === 'other' ? 1 : 0,
						height: watchedExitReason === 'other' ? 'auto' : '0px',
					}}
					transition={{ duration: 0.3, ease: 'easeInOut' }}
					style={{ overflow: 'hidden' }}
				>
					<TextArea
						control={control}
						name={'other-explanation'}
						placeholder={t('exitFeedback.formProcessing.reason.options.otherPlaceholder')}
						disabled={watchedExitReason !== 'other'}
					/>
				</motion.div>
			),
		},
	]

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleFormSubmit = (data: any) => {
		const exitValue = data['exit-reason']
		const translatedExitValue = options.find((option) => option.value === exitValue)?.label || exitValue

		const answer = `${translatedExitValue}${data['other-explanation'] ? `:${data['other-explanation']}` : ''}`

		trackExitEvent(t('exitFeedback.formProcessing.reason.label'), answer)

		if (onSubmit) {
			onSubmit()
		}
	}

	return (
		<form className={styles.form} onSubmit={handleSubmit(handleFormSubmit)}>
			<div className={styles.fields}>
				<RadioButtonGroup
					name="exit-reason"
					label={t('exitFeedback.formProcessing.reason.label')}
					options={options}
					className={styles.formGroup}
					register={register}
					registerOptions={{ required: t('exitFeedback.fieldRequired') }}
					error={errors['exit-reason'] && t('exitFeedback.fieldRequired')}
				/>
			</div>

			<div className={styles.buttons}>
				{exitButtonSlot}
				<Button className={styles.submit} type="primary" submit={true}>
					{t('exitFeedback.submit')}
				</Button>
			</div>
		</form>
	)
}
